<template>
  <section class="h-full mx-4">
    <BaseTable
      v-if="visitPlans"
      :headers="visitPlansHeaders"
      :list="visitPlans"
      :hide-grouping="true"
      clickable
      @table-item-clicked="onEditVisitPlan"
      @add-new="onAddNewVisitPlan"
      @deleteMultiselect="onDeleteMultiselect"
      @refresh-data="loadData"
      table-id="visit-plans"
      @apply-filters="onApplyFilters"
      @reset-filters="onResetFilters">
      <template #actions="{ item }: any">
        <div class="flex items-center justify-center gap-1">
          <ActionButton
            :isEnabled="item.isFromManager"
            color="red"
            :icon="IconTrashOutline"
            @action="onDeleteRow(item as VisitPlan)" />
        </div>
      </template>
      <template #filters>
        <section class="flex flex-col items-start justify-start w-full max-w-xl py-4 mx-auto">
          <div>
            <label class="space-y-2 text-sm tracking-wide text-gray-400 uppercase">{{
              $t('baseObject.isActive')
            }}</label>
            <BaseTriStateToggle
              :label="$t('baseObject.isActive')"
              name="isActive"
              v-model="filterIsActive" />
          </div>
        </section>
      </template>
    </BaseTable>

    <VisitPlanModalCreateEdit
      v-if="createEditModalActive"
      :visit-plan="visitPlan"
      :mode="createEditModalMode"
      :title="modalEditCreateTitle()"
      @close-modal="createEditModalActive = false"
      @save="onSave" />

    <BaseModalConfirm
      v-if="modalDeleteRow"
      @cancel="modalDeleteRow = false"
      @confirm="onDeleteSingle()" />
  </section>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { v4 as uuidv4 } from 'uuid';
import { useToastStore } from '@/stores/toast';
import { useI18n } from 'vue-i18n';

import { getVisitPlans, removeVisitPlansByIds } from '@/services/api/routes';
import { filterActive, filterData, sortData } from '@/utils/table';
import BaseTable from '@/components/common/BaseTable.vue';
import BaseModalConfirm from '@/components/common/BaseModalConfirm.vue';
import VisitPlanModalCreateEdit from '@/components/routes/VisitPlanModalCreateEdit.vue';
import IconTrashOutline from '@/assets/icons/IconTrashOutline.vue';
import ActionButton from '@/components/common/ActionButton.vue';
import BaseTriStateToggle from '@/components/common/BaseTriStateToggle.vue';

import type { TableFilterData } from '@/types/Base';
import type { VisitPlan } from '@/types/Routes';

const { t: $t } = useI18n({ useScope: 'global' });

const toastStore = useToastStore();

const visitPlans = ref<VisitPlan[]>([]);
const filterIsActive = ref<boolean | undefined>();
const visitPlansHeaders = [
  { value: 'name', text: $t('visitPlans.visitPlan'), type: 'text', active: true },
  { value: 'weekStep', text: $t('visitPlans.weeklyStep'), type: 'number', active: true },
  { value: 'effectiveWeek', text: $t('visitPlans.effectiveWeek'), type: 'number', active: true },
  { value: 'remarks', text: $t('visitPlans.remarks'), type: 'text', active: true },
  { value: 'actions', text: $t('baseObject.actions'), active: true },
];

onMounted(async () => {
  await loadData();
});

const visitPlan = ref<VisitPlan>(initializeVisitPlan());
const createEditModalActive = ref(false);
const createEditModalMode = ref<'add' | 'edit'>('add');

function initializeVisitPlan(): VisitPlan {
  return {
    id: uuidv4(),
    name: '',
    code: '',
    remarks: '',
    weekStep: 0,
    effectiveWeek: 0,
    isActive: true,
    isFromManager: true,
  };
}

function onAddNewVisitPlan() {
  visitPlan.value = initializeVisitPlan();
  createEditModalActive.value = true;
  createEditModalMode.value = 'add';
}

function onEditVisitPlan(item: VisitPlan) {
  visitPlan.value = item;
  createEditModalActive.value = true;
  createEditModalMode.value = 'edit';
}

function onSave(modifiedData: VisitPlan) {
  if (!visitPlan.value) return;

  const index = visitPlans.value.findIndex((item) => item.id === modifiedData.id);
  if (index !== -1) visitPlans.value[index] = modifiedData;
  else visitPlans.value.push(modifiedData);
  createEditModalActive.value = false;
}

async function loadData(data?: TableFilterData) {
  let response = await getVisitPlans();
  if (!response) return;

  if (data && data.query) {
    response = filterData(response, data.query, ['name', 'remarks', 'code']);
  }

  if (data && data.column !== undefined && data.order) {
    response = sortData(response, data.column, data.order, visitPlansHeaders);
  }

  if (data && data.isActive !== undefined) response = filterActive(response, data.isActive);

  visitPlans.value = response;
}

async function onDeleteMultiselect(items: VisitPlan[]) {
  const idsToDelete = items.filter((item) => item.isFromManager === true).map((item) => item.id);
  if (!idsToDelete.length) {
    toastStore.warning({ text: $t('messages.info.selectedItemsCannotBeDeleted') });
    return;
  }
  const res = await removeVisitPlansByIds(idsToDelete);
  if (!res && visitPlans.value)
    visitPlans.value = visitPlans.value.filter((type) => !idsToDelete.includes(type.id));
  res
    ? toastStore.error({ text: res })
    : toastStore.success({ text: $t('messages.success.successfullyDeleted') });
}

async function onDeleteSingle() {
  modalDeleteRow.value = false;
  if (!deleteRowItem.value) return;
  const idToDelete = deleteRowItem.value.id;
  const res = await removeVisitPlansByIds([idToDelete]);
  if (!res && visitPlans.value)
    visitPlans.value = visitPlans.value.filter((type) => type.id !== idToDelete);
  res
    ? toastStore.error({ text: res })
    : toastStore.success({ text: $t('messages.success.successfullyDeleted') });
}

const modalDeleteRow = ref(false);

const deleteRowItem = ref<VisitPlan>();
function onDeleteRow(item: VisitPlan) {
  deleteRowItem.value = item;
  modalDeleteRow.value = true;
}

function modalEditCreateTitle() {
  if (visitPlan.value && !visitPlan.value.isFromManager)
    return `${$t('common.actions.view')} ${$t('visitPlans.visitPlan2')}`;
  return createEditModalMode.value === 'edit'
    ? `${$t('common.actions.edit')} ${$t('visitPlans.visitPlan2')}`
    : `${$t('common.actions.create')} ${$t('visitPlans.visitPlan2')}`;
}

function onApplyFilters(data: TableFilterData) {
  data.isActive = filterIsActive.value ?? undefined;
  loadData(data);
}

function onResetFilters() {
  filterIsActive.value = undefined;
  loadData({});
}
</script>
